import React from 'react';
import { Link } from 'react-router-dom';
import Header from './Header';
import Footer from './Footer';

const LandingPage = () => {
  const features = [
    {
      icon: "🎯",
      title: "Any Topic Under the Sun",
      description: "From Marvel superheroes to quantum physics, create quizzes on literally anything you can think of!"
    },
    {
      icon: "⚡",
      title: "Ready in Seconds",
      description: "Our AI whips up engaging quizzes faster than you can say 'Quiz me!'"
    },
    {
      icon: "🎮",
      title: "Fun & Interactive",
      description: "Not your boring old quiz - enjoy dynamic questions, instant feedback, and fun challenges!"
    },
    {
      icon: "🏆",
      title: "Level Up",
      description: "Start easy, work your way up. Challenge yourself or challenge your friends!"
    }
  ];

  const popularTopics = [
    "Harry Potter",
    "Friends TV Show",
    "Marvel Universe",
    "World Cuisine",
    "Space Exploration",
    "K-pop Music",
    "Football",
    "Anime & Manga"
  ];

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-b from-indigo-50 via-white to-purple-50">
      <Header />
      
      {/* Hero Section */}
      <main className="flex-grow">
        <div className="relative overflow-hidden">
          {/* Decorative background elements */}
          <div className="absolute inset-0 z-0">
            <div className="absolute top-20 left-10 w-72 h-72 bg-pink-200 rounded-full mix-blend-multiply filter blur-xl opacity-30 animate-blob"></div>
            <div className="absolute top-40 right-10 w-72 h-72 bg-yellow-200 rounded-full mix-blend-multiply filter blur-xl opacity-30 animate-blob animation-delay-2000"></div>
            <div className="absolute -bottom-8 left-40 w-72 h-72 bg-blue-200 rounded-full mix-blend-multiply filter blur-xl opacity-30 animate-blob animation-delay-4000"></div>
          </div>

          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-24 sm:pt-24 relative z-10">
            <div className="text-center">
              <h1 className="text-5xl sm:text-7xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-indigo-600 to-purple-600 tracking-tight mb-6 animate-gradient">
                Quiz Time = Fun Time!
              </h1>
              <p className="max-w-2xl mx-auto text-xl sm:text-2xl text-gray-600 mb-8">
                Create awesome quizzes about <span className="text-indigo-600 font-semibold">anything</span> you love! 
                Whether you're a pop culture guru, science nerd, or just here for fun 🎉
              </p>

              {/* Popular Topics Pills */}
              <div className="mb-10">
                <div className="flex flex-wrap justify-center gap-2 items-center">
                  <span className="text-sm text-gray-500">Popular:</span>
                  {popularTopics.map((topic, index) => {
                    const emojis = {
                      "Harry Potter": "🪄",
                      "Friends TV Show": "📺",
                      "Marvel Universe": "⚡",
                      "World Cuisine": "🍜",
                      "Space Exploration": "🚀",
                      "K-pop Music": "🎵",
                      "Football": "⚽",
                      "Anime & Manga": "🎭"
                    };
                    return (
                      <div
                        key={index}
                        to={`/quiz?topic=${encodeURIComponent(topic)}`}
                        className="px-4 py-1.5 bg-white rounded-full border border-indigo-100 
                                 hover:border-indigo-300 hover:bg-indigo-50 transition-all duration-200 
                                 text-gray-700 hover:text-indigo-600 text-sm shadow-sm hover:shadow
                                 transform hover:-translate-y-0.5"
                      >
                        <span>{topic}</span>
                        <span className="ml-1">{emojis[topic]}</span>
                      </div>
                    );
                  })}
                </div>
              </div>

              <div className="flex flex-col sm:flex-row justify-center gap-4 mb-12">
                <Link
                  to="/quiz"
                  className="group relative inline-flex items-center justify-center px-8 py-4 
                           text-lg font-bold rounded-full text-white bg-gradient-to-r from-indigo-600 to-purple-600 
                           hover:from-indigo-500 hover:to-purple-500 transform transition-all duration-200 
                           hover:-translate-y-1 hover:shadow-lg overflow-hidden"
                >
                  <span className="relative z-10">Create Your Quiz</span>
                  {/* Animated background effect */}
                  <div className="absolute inset-0 w-full h-full bg-gradient-to-r from-indigo-600 to-purple-600 
                                transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left"></div>
                </Link>
                <a
                  href="#how-it-works"
                  className="inline-flex items-center justify-center px-8 py-4 text-lg font-bold 
                           rounded-full text-indigo-600 bg-white border-2 border-indigo-100
                           hover:border-indigo-300 hover:bg-indigo-50 transition-all duration-200
                           transform hover:-translate-y-1 hover:shadow-lg"
                >
                  See How It Works
                </a>
              </div>

              {/* Fun Stats Section */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 max-w-3xl mx-auto">
                {[
                  { number: "Instant", label: "Quiz Generation" },
                  { number: "Multiple", label: "Quiz Formats" },
                  { number: "∞", label: "Possible Quiz Topics" },
                  { number: "100%", label: "Fun Guaranteed" }
                ].map((stat, index) => (
                  <div key={index} className="bg-white rounded-xl p-4 shadow-sm border border-indigo-50 hover:border-indigo-200 transition-all duration-200">
                    <div className="text-2xl sm:text-3xl font-bold text-indigo-600">{stat.number}</div>
                    <div className="text-sm text-gray-600">{stat.label}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Features Section */}
        <section className="py-16 sm:py-24">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
                Why QuizCult is <span className="text-indigo-600">Awesome</span>
              </h2>
              <p className="text-xl text-gray-600">
                The most fun way to learn, challenge yourself, or just kill time! 
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
              {features.map((feature, index) => (
                <div 
                  key={index}
                  className="group bg-white p-6 rounded-xl border border-indigo-100 shadow-sm 
                           hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1"
                >
                  <div className="text-4xl mb-4 transform group-hover:scale-110 transition-transform duration-200">
                    {feature.icon}
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">
                    {feature.title}
                  </h3>
                  <p className="text-gray-600">
                    {feature.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* How It Works Section */}
        <section id="how-it-works" className="py-16 sm:py-24 bg-gradient-to-b from-white to-indigo-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
                3 Simple Steps to Quiz Heaven
              </h2>
              <p className="text-xl text-gray-600">
                No sign-up needed - just jump right in!
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {[
                {
                  step: 1,
                  title: "Pick Your Topic",
                  description: "Anything from 'Dogs 🐕' to 'Greek Mythology 🏺' - if you can think it, we can quiz it!",
                  icon: "🎯"
                },
                {
                  step: 2,
                  title: "Customize Your Quiz",
                  description: "Choose difficulty, number of questions, and type. Make it yours!",
                  icon: "⚙️"
                },
                {
                  step: 3,
                  title: "Have Fun!",
                  description: "Take the quiz, challenge friends, learn something new, and enjoy!",
                  icon: "🎉"
                }
              ].map((step) => (
                <div key={step.step} className="relative">
                  <div className="bg-white p-8 rounded-xl border border-indigo-100 shadow-sm hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1">
                    <div className="text-4xl mb-4">{step.icon}</div>
                    <div className="w-12 h-12 bg-gradient-to-r from-indigo-600 to-purple-600 text-white 
                                  rounded-full flex items-center justify-center text-xl font-bold mb-6">
                      {step.step}
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">
                      {step.title}
                    </h3>
                    <p className="text-gray-600">
                      {step.description}
                    </p>
                  </div>
                  {step.step < 3 && (
                    <div className="hidden md:block absolute top-1/2 right-0 transform translate-x-1/2 -translate-y-1/2 text-4xl text-indigo-300">
                      →
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 sm:py-24 relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-r from-indigo-600 to-purple-600 opacity-90"></div>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-8 animate-bounce">
              Ready to Have Some Fun? 🎮
            </h2>
            <Link
              to="/quiz"
              className="inline-flex items-center justify-center px-8 py-4 text-lg font-bold 
                       rounded-full bg-white text-indigo-600 hover:bg-gray-50 
                       transition-all duration-200 transform hover:-translate-y-1 hover:shadow-lg"
            >
              Create Your First Quiz
              <span className="ml-2 text-2xl">🚀</span>
            </Link>
          </div>
        </section>
      </main>
      
      <Footer />

      {/* Add required CSS for animations */}
      <style jsx>{`
        @keyframes blob {
          0% { transform: translate(0px, 0px) scale(1); }
          33% { transform: translate(30px, -50px) scale(1.1); }
          66% { transform: translate(-20px, 20px) scale(0.9); }
          100% { transform: translate(0px, 0px) scale(1); }
        }
        .animate-blob {
          animation: blob 7s infinite;
        }
        .animation-delay-2000 {
          animation-delay: 2s;
        }
        .animation-delay-4000 {
          animation-delay: 4s;
        }
        @keyframes gradient {
          0% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }
        .animate-gradient {
          background-size: 200% auto;
          animation: gradient 4s linear infinite;
        }
      `}</style>
    </div>
  );
};

export default LandingPage; 
